<template>
  <div v-if="modelValue" class="overlay" :class="overlayClass">
    <div class="container" :class="containerClass" :style="containerStyle">
      <div class="header">
        <h2>{{ title }}</h2>
        <button @click="$emit('update:modelValue', false)" class="close-btn">&times;</button>
      </div>

      <div class="content">
        <slot></slot>
        
        <!-- Footer moved inside content on mobile -->
        <div v-if="hasButtons" class="footer mobile-footer">
          <!-- Combined layout: Split Button + Regular Buttons -->
          <div v-if="hasSplitButton || hasRegularButtons" class="footer-row">
            <div class="footer-left">
              <!-- Split Button (if defined) -->
              <div v-if="hasSplitButton" class="split-button-container">
                <div class="split-button-group">
                  <!-- Main button (80%) -->
                  <button
                    :class="['split-button-main', ...(Array.isArray(splitButton.primary.class) ? splitButton.primary.class : [splitButton.primary.class])]"
                    :disabled="splitButton.primary.disabled"
                    @click="splitButton.primary.onClick">
                    {{ splitButton.primary.title }}
                  </button>
                  
                  <!-- Dropdown trigger (20%) -->
                  <button
                    :class="['split-button-dropdown', ...(Array.isArray(splitButton.primary.class) ? splitButton.primary.class : [splitButton.primary.class])]"
                    :disabled="splitButton.primary.disabled"
                    @click="!splitButton.primary.disabled && toggleSplitDropdown()" 
                    :aria-expanded="splitDropdownOpen">
                    <svg class="dropdown-icon" :class="{ 'rotated': splitDropdownOpen }"
                      xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </button>
                </div>

                <!-- Split button dropdown menu -->
                <div v-if="splitDropdownOpen && !splitButton.primary.disabled" class="split-dropdown-menu" @click.stop>
                  <div v-for="item in splitButton.dropdown" :key="item.title" class="dropdown-item"
                    @click="handleSplitDropdownItemClick(item)">
                    {{ item.title }}
                  </div>
                </div>
              </div>

              <!-- Regular Left Buttons -->
              <template v-for="button in leftButtons" :key="button.title">
                <!-- Regular button -->
                <button v-if="!button.type || button.type === 'button'"
                  :class="[ ...(Array.isArray(button.class) ? button.class : [button.class])]"
                  :disabled="button.disabled"
                  @click="button.onClick">
                  {{ button.title }}
                </button>

                <!-- Dropdown button -->
                <div v-else-if="button.type === 'dropdown'" class="dropdown-container" :key="button.title">
                  <button
                    :class="['dropdown-btn', ...(Array.isArray(button.class) ? button.class : [button.class])]"
                    :disabled="button.disabled"
                    @click="!button.disabled && toggleDropdown(button.title)" 
                    :aria-expanded="openDropdowns[button.title] || false">
                    {{ button.title }}
                    <svg class="dropdown-icon" :class="{ 'rotated': openDropdowns[button.title] }"
                      xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </button>

                  <div v-if="openDropdowns[button.title] && !button.disabled" class="dropdown-menu" @click.stop>
                    <div v-for="item in button.dropdownItems" :key="item.title" class="dropdown-item"
                      @click="handleDropdownItemClick(item, button.title)">
                      {{ item.title }}
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <div class="footer-right">
              <!-- Regular Right Buttons -->
              <template v-for="button in rightButtons" :key="button.title">
                <!-- Regular button -->
                <button v-if="!button.type || button.type === 'button'"
                  :class="[...(Array.isArray(button.class) ? button.class : [button.class])]"
                  :disabled="button.disabled"
                  @click="button.onClick"
                  style="min-width: 100px">
                  {{ button.title }}
                </button>

                <!-- Dropdown button -->
                <div v-else-if="button.type === 'dropdown'" class="dropdown-container" :key="button.title">
                  <button
                    :class="['dropdown-btn', ...(Array.isArray(button.class) ? button.class : [button.class])]"
                    :disabled="button.disabled"
                    @click="!button.disabled && toggleDropdown(button.title)" 
                    :aria-expanded="openDropdowns[button.title] || false">
                    {{ button.title }}
                    <svg class="dropdown-icon" :class="{ 'rotated': openDropdowns[button.title] }"
                      xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                  </button>

                  <div v-if="openDropdowns[button.title] && !button.disabled" class="dropdown-menu" @click.stop>
                    <div v-for="item in button.dropdownItems" :key="item.title" class="dropdown-item"
                      @click="handleDropdownItemClick(item, button.title)">
                      {{ item.title }}
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>

          <!-- Fallback: Center full-width buttons (when no split button or left/right buttons) -->
          <div v-else-if="centerButtons.length > 0" class="footer-center">
            <template v-for="button in centerButtons" :key="button.title">
              <button
                :class="['full-width', ...(Array.isArray(button.class) ? button.class : [button.class])]"
                :disabled="button.disabled"
                @click="button.onClick">
                {{ button.title }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Desktop footer - outside content, fixed at bottom -->
      <div v-if="hasButtons" class="footer desktop-footer">
        <!-- Combined layout: Split Button + Regular Buttons -->
        <div v-if="hasSplitButton || hasRegularButtons" class="footer-row">
          <div class="footer-left">
            <!-- Split Button (if defined) -->
            <div v-if="hasSplitButton" class="split-button-container">
              <div class="split-button-group">
                <!-- Main button (80%) -->
                <button
                  :class="['split-button-main', ...(Array.isArray(splitButton.primary.class) ? splitButton.primary.class : [splitButton.primary.class])]"
                  :disabled="splitButton.primary.disabled"
                  @click="splitButton.primary.onClick">
                  {{ splitButton.primary.title }}
                </button>
                
                <!-- Dropdown trigger (20%) -->
                <button
                  :class="['split-button-dropdown', ...(Array.isArray(splitButton.primary.class) ? splitButton.primary.class : [splitButton.primary.class])]"
                  :disabled="splitButton.primary.disabled"
                  @click="!splitButton.primary.disabled && toggleSplitDropdown()" 
                  :aria-expanded="splitDropdownOpen">
                  <svg class="dropdown-icon" :class="{ 'rotated': splitDropdownOpen }"
                    xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>
              </div>

              <!-- Split button dropdown menu -->
              <div v-if="splitDropdownOpen && !splitButton.primary.disabled" class="split-dropdown-menu" @click.stop>
                <div v-for="item in splitButton.dropdown" :key="item.title" class="dropdown-item"
                  @click="handleSplitDropdownItemClick(item)">
                  {{ item.title }}
                </div>
              </div>
            </div>

            <!-- Regular Left Buttons -->
            <template v-for="button in leftButtons" :key="button.title">
              <!-- Regular button -->
              <button v-if="!button.type || button.type === 'button'"
                :class="[ ...(Array.isArray(button.class) ? button.class : [button.class])]"
                :disabled="button.disabled"
                @click="button.onClick">
                {{ button.title }}
              </button>

              <!-- Dropdown button -->
              <div v-else-if="button.type === 'dropdown'" class="dropdown-container" :key="button.title">
                <button
                  :class="['dropdown-btn', ...(Array.isArray(button.class) ? button.class : [button.class])]"
                  :disabled="button.disabled"
                  @click="!button.disabled && toggleDropdown(button.title)" 
                  :aria-expanded="openDropdowns[button.title] || false">
                  {{ button.title }}
                  <svg class="dropdown-icon" :class="{ 'rotated': openDropdowns[button.title] }"
                    xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>

                <div v-if="openDropdowns[button.title] && !button.disabled" class="dropdown-menu" @click.stop>
                  <div v-for="item in button.dropdownItems" :key="item.title" class="dropdown-item"
                    @click="handleDropdownItemClick(item, button.title)">
                    {{ item.title }}
                  </div>
                </div>
              </div>
            </template>
          </div>

          <div class="footer-right">
            <!-- Regular Right Buttons -->
            <template v-for="button in rightButtons" :key="button.title">
              <!-- Regular button -->
              <button v-if="!button.type || button.type === 'button'"
                :class="[...(Array.isArray(button.class) ? button.class : [button.class])]"
                :disabled="button.disabled"
                @click="button.onClick"
                style="min-width: 100px">
                {{ button.title }}
              </button>

              <!-- Dropdown button -->
              <div v-else-if="button.type === 'dropdown'" class="dropdown-container" :key="button.title">
                <button
                  :class="['dropdown-btn', ...(Array.isArray(button.class) ? button.class : [button.class])]"
                  :disabled="button.disabled"
                  @click="!button.disabled && toggleDropdown(button.title)" 
                  :aria-expanded="openDropdowns[button.title] || false">
                  {{ button.title }}
                  <svg class="dropdown-icon" :class="{ 'rotated': openDropdowns[button.title] }"
                    xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6,9 12,15 18,9"></polyline>
                  </svg>
                </button>

                <div v-if="openDropdowns[button.title] && !button.disabled" class="dropdown-menu" @click.stop>
                  <div v-for="item in button.dropdownItems" :key="item.title" class="dropdown-item"
                    @click="handleDropdownItemClick(item, button.title)">
                    {{ item.title }}
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

        <!-- Fallback: Center full-width buttons (when no split button or left/right buttons) -->
        <div v-else-if="centerButtons.length > 0" class="footer-center">
          <template v-for="button in centerButtons" :key="button.title">
            <button
              :class="['full-width', ...(Array.isArray(button.class) ? button.class : [button.class])]"
              :disabled="button.disabled"
              @click="button.onClick">
              {{ button.title }}
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseModal',
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    type: {
      type: String,
      default: 'modal',
      validator: (value) => ['modal', 'drawer'].includes(value)
    },
    width: {
      type: Number,
      default: 400
    },
    height: {
      type: Number,
      required: false
    },
    title: {
      type: String,
      required: true
    },
    buttons: {
      type: Array,
      default: () => [],
      validator: (value) => {
        return value.every(button => {
          const hasRequiredProps = typeof button.title === 'string' &&
            ['left', 'right', 'center'].includes(button.position);

          if (!button.type || button.type === 'button') {
            return hasRequiredProps && typeof button.onClick === 'function';
          } else if (button.type === 'dropdown') {
            return hasRequiredProps &&
              Array.isArray(button.dropdownItems) &&
              button.dropdownItems.every(item =>
                typeof item.title === 'string' &&
                typeof item.onClick === 'function'
              );
          }

          return false;
        });
      }
    },
    splitButton: {
      type: Object,
      default: null,
      validator: (value) => {
        if (!value) return true;
        
        return value.primary &&
          typeof value.primary.title === 'string' &&
          typeof value.primary.onClick === 'function' &&
          Array.isArray(value.dropdown) &&
          value.dropdown.every(item =>
            typeof item.title === 'string' &&
            typeof item.onClick === 'function'
          );
      }
    }
  },
  data() {
    return {
      openDropdowns: {},
      splitDropdownOpen: false,
      scrollPosition: 0
    };
  },
  computed: {
    hasButtons() {
      return this.buttons.length > 0 || this.hasSplitButton;
    },
    hasSplitButton() {
      return this.splitButton && this.splitButton.primary && this.splitButton.dropdown.length > 0;
    },
    hasRegularButtons() {
      return this.leftButtons.length > 0 || this.rightButtons.length > 0;
    },
    leftButtons() {
      return this.buttons.filter(button => button.position === 'left');
    },
    rightButtons() {
      return this.buttons.filter(button => button.position === 'right');
    },
    centerButtons() {
      return this.buttons.filter(button => button.position === 'center');
    },
    overlayClass() {
      return {
        'modal-overlay': this.type === 'modal',
        'drawer-overlay': this.type === 'drawer'
      };
    },
    containerClass() {
      return {
        'modal': this.type === 'modal',
        'drawer': this.type === 'drawer'
      };
    },
    containerStyle() {
      if (this.type === 'modal') {
        const style = { maxWidth: this.width + 'px' };
        if (this.height) {
          style.height = this.height + 'px';
        }
        return style;
      } else if (this.type === 'drawer') {
        const style = { 
          width: '100%',
          maxWidth: this.width + 'px' 
        };
        if (this.height) {
          style.height = this.height + 'px';
        }
        return style;
      }
      return {};
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    if (this.modelValue) {
      this.unlockBodyScroll();
    }
  },
  watch: {
    modelValue(newVal) {
      if (newVal) {
        this.lockBodyScroll();
      } else {
        this.unlockBodyScroll();
      }
    }
  },
  methods: {
    toggleDropdown(buttonTitle) {
      const newOpenDropdowns = {};
      newOpenDropdowns[buttonTitle] = !this.openDropdowns[buttonTitle];
      this.openDropdowns = newOpenDropdowns;
      this.splitDropdownOpen = false;
    },
    toggleSplitDropdown() {
      this.splitDropdownOpen = !this.splitDropdownOpen;
      this.openDropdowns = {};
    },
    handleDropdownItemClick(item, buttonTitle) {
      item.onClick();
      this.openDropdowns = {
        ...this.openDropdowns,
        [buttonTitle]: false
      };
    },
    handleSplitDropdownItemClick(item) {
      item.onClick();
      this.splitDropdownOpen = false;
    },
    handleClickOutside(event) {
      const dropdownContainer = event.target.closest('.dropdown-container');
      const splitButtonContainer = event.target.closest('.split-button-container');
      
      if (!dropdownContainer && !splitButtonContainer) {
        this.openDropdowns = {};
        this.splitDropdownOpen = false;
      }
    },
    lockBodyScroll() {
      // Save current scroll position
      this.scrollPosition = window.pageYOffset;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    },
    unlockBodyScroll() {
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      // Restore scroll position
      window.scrollTo(0, this.scrollPosition || 0);
    }
  },
  emits: ['update:modelValue']
}
</script>

<style scoped>
/* Base overlay styles */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1019;
  background-color: rgba(0, 0, 0, 0.5);
}

/* Modal-specific overlay */
.modal-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Drawer-specific overlay */
.drawer-overlay {
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
}

/* Base container styles */
.container {
  background-color: var(--background-table);
  display: flex;
  flex-direction: column;
  max-height: 100vh;
  max-height: 100dvh;
}

/* Modal-specific container */
.modal {
  border-radius: 32px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow: hidden;
}

/* Drawer-specific container */
.drawer {
  width: 400px;
  border-radius: 0;
  box-shadow: -4px 0 8px rgba(0, 0, 0, 0.1);
  transform: translateX(0);
  animation: slideInFromRight 0.3s ease-out;
}

@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

.drawer.closing {
  animation: slideOutToRight 0.3s ease-in forwards;
}

@keyframes slideOutToRight {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
}

/* Header styles */
.header {
  flex: 0 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header h2 {
  margin: 0;
  font-weight: 400;
}

.drawer .header {
    padding: 50px 50px 10px 50px;
}

.drawer .header h2 {
    font-size: 2rem;
}

.modal .header {
    padding: 1rem 3rem 0.75rem 3rem;
    border-bottom: 1px solid var(--border-color);
}

.modal .header h2 {
    font-size: 1.15rem;
}

/* Content styles */
.content {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.drawer .content {
  padding: 1rem 50px 0 50px;
}

.modal .content {
  padding: 1rem 3rem;
  min-height: 100px;
}

/* Footer styles */
.footer {
  flex: 0 0 auto;
  padding: 10px 3rem;
  margin-top: 10px;
  border-top: 1px solid var(--border-color)
}

.footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-left,
.footer-right {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
}

.footer-center {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0.5rem;
}

.full-width {
  width: 100%;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--text-color-secondary);
  cursor: pointer;
  padding: 0.5rem;
  margin: -0.5rem -0.5rem -0.5rem 0;
  line-height: 1;
}

.close-btn:hover {
  color: #333;
}

/* Split Button Styles */
.split-button-container {
  position: relative;
  display: inline-block;
}

.split-button-group {
  display: flex;
}

.split-button-main {
  border-radius: 50px 0 0 50px;
  border-right: 1px solid var(--border-color);
  min-width: 80px;
}

.split-button-dropdown {
  border-radius: 0 50px 50px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  padding-left: 20px;
}

.split-button-main:disabled,
.split-button-dropdown:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

.split-dropdown-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  min-width: 160px;
  background-color: var(--background-table);
  border: 1px solid var(--border-color);
  border-radius: 16px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 50;
  margin-bottom: 4px;
  white-space: nowrap;
  padding: 8px;
}

/* Dropdown Styles */
.dropdown-container {
  position: relative;
  display: inline-block;
}

.dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dropdown-icon {
  transition: transform 0.2s ease;
}

.dropdown-icon.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  bottom: 100%;
  left: 0;
  min-width: 160px;
  background-color: white;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 50;
  margin-bottom: 4px;
  padding: 4px 0;
}

.dropdown-item {
  padding: 12px 16px;
  border-radius: 50px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  white-space: nowrap;
}

.dropdown-item:hover {
  background-color: var(--selected-item-background);
}

/* Desktop vs Mobile Footer Display */
.desktop-footer {
  display: block;
}

.mobile-footer {
  display: none;
}

@media (max-width: 1440px) {
  .drawer .header {
    padding: 30px 50px 10px 50px;
  }

  .drawer .header h2 {
      font-size: 1.75rem;
  }

  .footer {
    padding: 8px 3rem;
  }

  .footer-right {
    gap: 0.25rem;
  }
}

/* Mobile styles */
@media (max-width: 768px) { 
  .drawer .header {
    padding-top: 30px;
  }

  .container.modal  {
    margin-left: 10px;
    margin-right: 10px;
  }

  .modal .header {
    padding-left: 2rem;
    padding-right: 2rem;
  }

  .modal .content {
    padding-left: 2rem;
    padding-right: 2rem;
  }
}

@media (max-width: 580px) { 
    /* Hide desktop footer, show mobile footer */
  .desktop-footer {
    display: none;
  }

  .footer {
    border-top: none;
  }
  
  .mobile-footer {
    display: block;
    margin-top: 0;
    padding-top: 1rem;
    padding-bottom: 2rem;
  }

  .footer-row {
    flex-direction: column;
    gap: 0.75rem;
    align-items: stretch;
  }
  
  .footer-left,
  .footer-right {
    display: contents;
  }

  /* Order individual buttons */
  .footer-right button.primary-btn {
    order: 1;
  }
  
  .split-button-container {
    order: 2;
  }
  
  .footer-left button.primary-btn {
    order: 3;
  }
  
  .footer-right button.secondary-btn,
  .footer-left button.secondary-btn {
    order: 5;
  }
  
  /* Make all buttons full width */
  .split-button-container {
    width: 100%;
  }
  
  /* Split button group layout */
  .split-button-group {
    width: 100%;
    display: flex;
  }
  
  .split-button-main {
    flex: 1;
    min-width: 0;
    border-radius: 50px 0 0 50px;
    border-right: 1px solid var(--border-color);
    text-align: center;
    padding-left: 64px;
    padding-right: 20px;
  }
  
  .split-button-dropdown {
    flex: 0 0 44px;
    width: 44px;
    min-width: 44px;
    max-width: 44px;
    border-radius: 0 50px 50px 0;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .split-dropdown-menu {
    left: auto;
    right: 0;
    transform-origin: top right;
    max-width: calc(100vw - 32px);
  }

  .drawer .header,
  .drawer .content,
  .drawer .footer {
    padding: 20px;
  }

  .drawer .header {
    padding-top: 12px;
    padding-bottom: 8px;
  }

  .modal .header {
    padding-top: 0.75rem;
    padding-bottom: 0.5rem;
  }

  .header .close-btn {
    font-size: 1.75rem;
  }

  .drawer .header h2 {
    font-size: 1.25rem;
  }
}

@media (max-width: 400px) {
  .send-email-btn {
    margin-top: -30px;
  }
}
</style>